// ===========================================
// FileField.jsx
// Componente universal de archivo (bytea) — MAPASAC ERP v2026
// Usado por SqlexecBuilder para field.type === 'file'
// Igual que ImageField pero sin preview de imagen: muestra el nombre
// del archivo y acepta cualquier extensión definida en field.accept
// (ej. ".cer,.key,.pem")
// ===========================================

import React, { useRef } from 'react';
import { File as FileIcon, FileCheck2, Upload, Trash2 } from 'lucide-react';
import { toast } from 'react-toastify';

// ===========================================
// FileField
// Props:
//   field            — { name, label, required, gridSpan, maxSizeMb, accept }
//   previewBase64    — base64 actual (archivo existente o recién elegido)
//   previewMime      — mime type de previewBase64
//   previewFilename  — nombre de archivo a mostrar (existente o recién elegido)
//   onChange         — (base64OrNull, mime, filename) => void
//                        null, null, null → usuario presionó "Quitar archivo"
//   disabled         — readonly (modo view)
//   showError / errorMessage
//   labelStyles / inputStyles / errorStyles — estilos desde sisconfig (formulario_ui)
//   btnBase / btnPrimary / btnSecondary — estilos de botones desde sisconfig (botones_ui);
//   btnBase.danger/.success se usan internamente para "Quitar archivo" y el ícono de archivo cargado
// ===========================================
const FileField = ({
  field,
  previewBase64,
  previewFilename,
  onChange,
  disabled = false,
  showError = false,
  errorMessage = null,
  labelStyles = {},
  inputStyles = {},
  errorStyles = {},
  btnBase = {},
  btnPrimary: _btnPrimary = {},
  btnSecondary = {}
}) => {
  const fileInputRef = useRef(null);
  const btnDanger = btnBase.danger || {};
  const btnSuccess = btnBase.success || {};
  const maxSizeMb = field?.maxSizeMb || 5;
  const accept = field?.accept || '*';
  const acceptList = accept.split(',').map(s => s.trim().toLowerCase()).filter(Boolean);

  const isExtensionPermitida = (filename) => {
    if (acceptList.length === 0 || acceptList.includes('*')) return true;
    const lower = filename.toLowerCase();
    return acceptList.some(ext => lower.endsWith(ext));
  };

  const handleFileSelected = (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // permite re-seleccionar el mismo archivo

    if (!file) return;

    if (!isExtensionPermitida(file.name)) {
      toast.error(`Extensión no permitida: ${file.name} (permitidas: ${accept})`);
      return;
    }
    if (file.size / (1024 * 1024) > maxSizeMb) {
      toast.error(`Archivo demasiado grande (máximo ${maxSizeMb} MB)`);
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result.split(',')[1];
      onChange?.(base64, file.type || 'application/octet-stream', file.name);
    };
    reader.onerror = () => toast.error('No se pudo leer el archivo');
    reader.readAsDataURL(file);
  };

  const handleQuitarArchivo = () => {
    onChange?.(null, null, null);
  };

  const nombreMostrado = previewFilename || (previewBase64 ? 'Archivo cargado' : null);

  return (
    <div>
      <div
        className="flex items-center justify-between"
        style={{ marginBottom: labelStyles.marginBottom || '0.5rem' }}
      >
        <label
          style={{
            fontSize: labelStyles.fontSize || '0.875rem',
            fontWeight: labelStyles.fontWeight || '500',
            color: labelStyles.textColor || '#374151'
          }}
        >
          {field?.label}{!disabled && field?.required && <span className="text-red-500 ml-1">*</span>}
        </label>

        {!disabled && (
          <div className="flex items-center gap-2">
            {!nombreMostrado && (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                title="Seleccionar archivo"
                className="flex items-center gap-1"
                style={{
                  fontSize: btnBase.fontSize || '0.75rem',
                  fontFamily: btnBase.fontFamily || 'inherit',
                  paddingLeft: btnBase.padding?.x || '1rem',
                  paddingRight: btnBase.padding?.x || '1rem',
                  paddingTop: btnBase.padding?.y || '0.5rem',
                  paddingBottom: btnBase.padding?.y || '0.5rem',
                  borderRadius: btnBase.borderRadius || '6px',
                  borderWidth: btnBase.borderWidth || '1px',
                  borderStyle: 'solid',
                  borderColor: btnSecondary.borderColor || '#d1d5db',
                  backgroundColor: btnSecondary.backgroundColor || '#ffffff',
                  color: btnSecondary.textColor || '#374151',
                  cursor: 'pointer'
                }}
              >
                <Upload size={12} />
                Seleccionar archivo
              </button>
            )}

            {nombreMostrado && (
              <button
                type="button"
                onClick={handleQuitarArchivo}
                title="Quitar archivo"
                className="flex items-center gap-1"
                style={{
                  fontSize: btnBase.fontSize || '0.75rem',
                  fontFamily: btnBase.fontFamily || 'inherit',
                  paddingLeft: btnBase.padding?.x || '1rem',
                  paddingRight: btnBase.padding?.x || '1rem',
                  paddingTop: btnBase.padding?.y || '0.5rem',
                  paddingBottom: btnBase.padding?.y || '0.5rem',
                  borderRadius: btnBase.borderRadius || '6px',
                  borderWidth: btnBase.borderWidth || '1px',
                  borderStyle: 'solid',
                  borderColor: btnDanger.borderColor || '#dc2626',
                  backgroundColor: btnDanger.backgroundColor || '#dc2626',
                  color: btnDanger.textColor || '#ffffff',
                  cursor: 'pointer'
                }}
              >
                <Trash2 size={12} />
                Quitar archivo
              </button>
            )}
          </div>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        onChange={handleFileSelected}
        style={{ display: 'none' }}
      />

      <div
        style={{
          borderWidth: inputStyles.borderWidth || '1px',
          borderStyle: 'solid',
          borderColor: inputStyles.borderColor || '#d1d5db',
          borderRadius: inputStyles.borderRadius || '6px',
          backgroundColor: inputStyles.backgroundColor || '#ffffff',
          padding: inputStyles.padding?.x || '0.75rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          minHeight: '48px'
        }}
      >
        {nombreMostrado ? (
          <>
            <FileCheck2 size={18} style={{ color: btnSuccess.backgroundColor || '#16a34a', flexShrink: 0 }} />
            <span style={{ fontSize: inputStyles.fontSize || '0.75rem', color: labelStyles.textColor || '#374151', wordBreak: 'break-all' }}>
              {nombreMostrado}
            </span>
          </>
        ) : (
          <>
            <FileIcon size={18} style={{ color: inputStyles.placeholderColor || '#9ca3af', flexShrink: 0 }} />
            <span style={{ fontSize: inputStyles.fontSize || '0.75rem', fontStyle: 'italic', color: inputStyles.placeholderColor || '#9ca3af' }}>
              Sin archivo
            </span>
          </>
        )}
      </div>

      {showError && (
        <p
          style={{
            fontSize: errorStyles.fontSize || '0.75rem',
            color: errorStyles.textColor || '#dc2626',
            marginTop: errorStyles.marginTop || '0.25rem'
          }}
        >
          {errorMessage}
        </p>
      )}
    </div>
  );
};

export default FileField;
